import React, { useEffect, useState } from 'react';
import { Modal } from '@ofself/shell';
import api from '../../services/api';
import { PersonFace } from '../../components/nucleus/faces';
import Segmented from '../../components/nucleus/Segmented';
import { SheetFoot, FootButton } from '../../components/nucleus/SheetFoot';
import { FindBar } from '../../components/nucleus/FacetRail';
import { noAccessRealm, fullAccessRealm } from '../nucleus/PeopleGroupData';
import RealmChoice from './RealmChoice';
import '../nucleus/PeoplePage.css'; // the sheet's rows and field
import './InviteSheet.css';

const ROLES = [
    { value: 'member', label: 'Member' },
    { value: 'admin', label: 'Admin' },
];

// INVITE TO THE GROUP (nucleus.oS Groups.swift InviteSheet): anyone on ofself by
// username, minus the people already in it, as a member or as an admin. A
// member is invited onto one of the group's realms (what they can do), each said
// in a line, No access until the admin picks another; an admin is always on Full
// Access. Picking someone sends the invitation (the page wraps the group key for
// them when it can). What they share is theirs to choose when they join.
export default function InviteSheet({ open, onClose, memberIds, pendingNames, realms = [], groupName = 'the group', onInvite }) {
    const [q, setQ] = useState('');
    const [found, setFound] = useState([]);
    const [sent, setSent] = useState(new Set());
    const [role, setRole] = useState('member');
    // No access, always; never another realm in its place (an invitation sent
    // with none lands on the group's No access too)
    const startRealm = () => noAccessRealm(realms)?.id || '';
    const [realmId, setRealmId] = useState(startRealm);

    useEffect(() => {
        if (!open) { setQ(''); setFound([]); setSent(new Set()); setRole('member'); setRealmId(startRealm()); return undefined; }
        let live = true;
        const t = setTimeout(() => {
            const term = q.trim();
            if (!term) { setFound([]); return; }
            api.searchUsers(term, 20).then((r) => { if (live) setFound(Array.isArray(r) ? r : r?.users || []); }).catch(() => { if (live) setFound([]); });
        }, 250);
        return () => { live = false; clearTimeout(t); };
    }, [q, open]); // eslint-disable-line react-hooks/exhaustive-deps
    // the realms land after the sheet: No access, once they do
    useEffect(() => { if (!realmId || !realms.some((r) => r.id === realmId)) setRealmId(startRealm()); }, [realms]); // eslint-disable-line react-hooks/exhaustive-deps

    const invite = async (id, name) => {
        // an admin is always on Full Access: the server puts them there, nothing to name
        const ok = await onInvite(id, name, role, role === 'admin' ? null : realmId || null);
        if (ok !== false) setSent((s) => new Set(s).add(id));
    };

    return (
        <Modal open={open} onClose={onClose} title="Invite to the group" size="md"
            lead={`They get an invitation that shows both sides: what they can do in ${groupName}, and that they choose what they share.`}>
            <div className="people-invite__role">
                <span className="people-invite__label">Invite as</span>
                <Segmented label="Invite as" options={ROLES} value={role} onChange={setRole} />
            </div>
            {realms.length > 0 && (
                <div className="people-invite__role">
                    <span className="people-invite__label">What they can do in {groupName}</span>
                    {/* an admin is always on Full Access: shown, not chosen */}
                    <RealmChoice realms={role === 'admin' ? [fullAccessRealm(realms)].filter(Boolean) : realms}
                        value={role === 'admin' ? fullAccessRealm(realms)?.id : realmId}
                        disabled={role === 'admin'} groupName={groupName} onChange={setRealmId} />
                    <span className="people-dialog__note">They choose what they share with {groupName} when they join. An admin is always on Full Access.</span>
                </div>
            )}
            <FindBar compact autoFocus value={q} onChange={setQ} placeholder="Username" />
            <div className="people-add__list">
                {found.map((u) => {
                    const id = String(u.user_id || u.id);
                    const name = u.username || 'Someone';
                    const member = memberIds.has(id);
                    const waiting = sent.has(id) || pendingNames.has(name);
                    return (
                        <div key={id} className="people-add__row">
                            <PersonFace size={30} />
                            <span className="people-add__name">{name}</span>
                            {member ? <span className="people-add__tag">member</span>
                                : waiting ? <span className="people-add__tag">invited</span>
                                    : <FootButton primary onClick={() => invite(id, name)}>{role === 'admin' ? 'Invite as admin' : 'Invite'}</FootButton>}
                        </div>
                    );
                })}
                {found.length === 0 && q.trim() && <p className="people-add__none">Nobody by that name</p>}
            </div>
            <SheetFoot>
                <FootButton primary onClick={onClose}>Done</FootButton>
            </SheetFoot>
        </Modal>
    );
}
